import { defineSchema } from '../../schema-meta.ts';
import { registerCategorySchema } from '../../registry.ts';
import { registerSchema } from '../../generic-codec.ts';
import type { AllRawData } from '../../raw-types.ts';

/**
 * Per-metaTag synergy breakpoint. `level` is the activation threshold (number
 * of pets carrying the tag that are required to unlock this row's bonus).
 * `hpBonus` is included for forward-compat with the HP-buff design — current
 * dev data has 0 HP in `meta_tag_synergy.synergy_stats` but the slot is
 * reserved so authors can introduce HP-bonus synergies later without another
 * codec migration.
 */
const MetaTagSynergySchema = defineSchema({
  name: 'metaTagSynergy',
  fields: [
    ['level', 'number'],
    ['hpBonus', 'number?'],
  ],
});
registerSchema(MetaTagSynergySchema);

/**
 * Server-side metaTag catalogue. Used by server score code to:
 *   1. Count `achievedSynergyTiers` — sum of synergy breakpoints reached
 *      across all equipped pets (used by `calcScore`'s synergy multiplier).
 *   2. Compute the HP bonus from active synergies (once authors fill it in).
 *
 * Client-only display fields (name, icon, description) are omitted — they
 * live in `clientMetaTags` and never reach this seed.
 */
export const MetaTagSchema = defineSchema({
  name: 'serverMetaTags',
  fields: [
    ['id', 'number'],
    ['synergies', { kind: 'array', of: 'metaTagSynergy' }],
  ],
  lookupKeys: ['id'],
});

export interface RawMetaTagRow {
  id: number;
}

function numericHp(value: unknown): number {
  if (value == null) return 0;
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
}

export function metaTagFromRaw(row: RawMetaTagRow, raw: Pick<AllRawData, 'meta_tag_synergy'>) {
  const synergies = (raw.meta_tag_synergy ?? [])
    .filter((s) => s.meta_tag_id === row.id)
    .sort((a, b) => a.level - b.level)
    .map((s) => {
      const stats = s.synergy_stats as Record<string, unknown> | null | undefined;
      return {
        level: s.level,
        hpBonus: numericHp(stats?.HP),
      };
    });
  return { id: row.id, synergies };
}

registerCategorySchema('serverMetaTags', MetaTagSchema);
